import { useCallback, useEffect, useState } from "react";
import type { ShopPage } from "../../types/shopMerchant";

export function useShopList<TItem, TQuery>(
  loader: (query: TQuery, signal: AbortSignal) => Promise<ShopPage<TItem>>,
  query: TQuery
): {
  rows: TItem[];
  total: number;
  loading: boolean;
  failed: boolean;
  refresh: () => void;
} {
  const [result, setResult] = useState<{ rows: TItem[]; total: number }>({
    rows: [],
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(
    (): void => setRevision((value) => value + 1),
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    void loader(query, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted)
          setResult({ rows: response.rows ?? [], total: response.total ?? 0 });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setFailed(true);
          setResult({ rows: [], total: 0 });
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return (): void => controller.abort();
  }, [loader, query, revision]);

  return { ...result, loading, failed, refresh };
}
